import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { shippingApi } from './api';

function requestKey() {
  if (!window.crypto?.getRandomValues) throw new Error('Secure request IDs require a supported browser over HTTPS.');
  return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
}

export default function VisualReviewPanel({ evidence, csrfToken, disabled, onAccessLost, onBusy }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef(null);
  useEffect(() => {
    onBusy?.(evidence.evidence_key, busy);
    return () => onBusy?.(evidence.evidence_key, false);
  }, [onBusy, evidence.evidence_key, busy]);

  const load = useCallback(async () => {
    controller.current?.abort();
    controller.current = new AbortController();
    try {
      setState(await shippingApi.visualChecks(evidence.evidence_key, controller.current.signal));
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else if (err.status !== 503) setError(err.message || 'Visual review unavailable.');
    }
  }, [evidence.evidence_key, onAccessLost]);

  useEffect(() => {
    load();
    return () => controller.current?.abort();
  }, [load]);

  async function run(kind) {
    setBusy(true); setError('');
    try {
      if (kind === 'QUALITY') await shippingApi.qualityCheck(evidence.evidence_key, { request_key: requestKey() }, csrfToken);
      else await shippingApi.visualCheck(evidence.evidence_key, { request_key: requestKey() }, csrfToken);
      await load();
    } catch (err) {
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Visual review failed.');
    } finally { setBusy(false); }
  }

  async function disposition(check, value) {
    setBusy(true); setError('');
    try {
      await shippingApi.visualDisposition(evidence.evidence_key, check.check_key,
        { request_key: requestKey(), disposition: value }, csrfToken);
      await load();
    } catch (err) {
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Could not record review disposition.');
    } finally { setBusy(false); }
  }

  const checks = state?.checks || [];
  if (!state || (!state.quality_enabled && !state.ai_enabled)) return null;
  return <Paper variant="outlined" sx={{ p: 1.25, mt: 1 }}><Stack spacing={1}>
    <Typography variant="subtitle2">Photo quality & advisory review</Typography>
    <Typography variant="caption">Optional assistance only. Barcode/package verification remains authoritative and visual results never advance this job.</Typography>
    {error && <Alert severity="warning">{error}</Alert>}
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {state?.quality_enabled && <Button size="small" disabled={disabled || busy} onClick={() => run('QUALITY')}>Check photo quality</Button>}
      {state?.ai_enabled && <Button size="small" disabled={disabled || busy || !evidence.buyer_copy_available} onClick={() => run('VISUAL')}>Advisory card review</Button>}
    </Stack>
    {state?.ai_enabled && !evidence.buyer_copy_available && <Typography variant="caption">AI review requires the separately approved contents-only crop. The full packing photo is never forwarded.</Typography>}
    {checks.map(check => <Paper key={check.check_key} variant="outlined" sx={{ p: 1 }}><Stack spacing={0.5}>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" label={`${check.kind} · ${check.status}`} color={check.status === 'RETAKE' ? 'warning' : check.status === 'PASS' ? 'success' : 'default'} />
        <Typography variant="caption">{check.engine_version}</Typography>
      </Stack>
      {check.result?.reasons?.length > 0 && <Typography variant="body2">Review: {check.result.reasons.join(', ')}</Typography>}
      {check.result?.warnings?.length > 0 && <Typography variant="body2">Warnings: {check.result.warnings.join(', ')}</Typography>}
      {check.kind === 'VISUAL' && <>
        <Typography variant="body2">Advisory only · {check.result?.assessment || 'REVIEW'}{check.confidence != null ? ` · confidence ${Math.round(check.confidence * 100)}%` : ''}</Typography>
        {check.visible_candidates?.slice(0, 6).map((candidate, index) => <Typography key={index} variant="caption">{index + 1}. {candidate.query || candidate.card_name_guess || 'Uncertain visible card'} · {Math.round((candidate.confidence || 0) * 100)}%</Typography>)}
      </>}
      {check.operator_disposition === 'PENDING' ? <Stack direction="row" gap={1} flexWrap="wrap">
        <Button size="small" disabled={busy || disabled} onClick={() => disposition(check, 'ACCEPT_ADVISORY')}>{check.kind === 'QUALITY' ? 'Reviewed · quality acceptable' : 'Reviewed · advisory acceptable'}</Button>
        <Button size="small" disabled={busy || disabled} onClick={() => disposition(check, 'RETAKE')}>Retake photo</Button>
        <Button size="small" disabled={busy || disabled} onClick={() => disposition(check, 'REVIEWED_UNCERTAIN')}>Reviewed · uncertain</Button>
      </Stack> : <Typography variant="caption">Operator disposition: {check.operator_disposition}</Typography>}
    </Stack></Paper>)}
  </Stack></Paper>;
}
